import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  Button,
  Text,
  Persona,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Divider,
  makeStyles,
} from '@fluentui/react-components';
import {
  Home24Regular,
  People24Regular,
  DocumentText24Regular,
  ClipboardTask24Regular,
  Shield24Regular,
  Settings24Regular,
  SignOut24Regular,
  CheckboxChecked24Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Navigation24Regular,
} from '@fluentui/react-icons';

const MOBILE = '@media (max-width: 768px)';

interface LayoutProps {
  children: React.ReactNode;
}

const navSections = [
  {
    label: 'Survey Operations',
    items: [
      { text: 'Dashboard', icon: Home24Regular, path: '/dashboard' },
      { text: 'Surveys', icon: DocumentText24Regular, path: '/surveys' },
      { text: 'Users', icon: People24Regular, path: '/users' },
      { text: 'Assignments', icon: ClipboardTask24Regular, path: '/assignments' },
      { text: 'Survey Status', icon: CheckboxChecked24Regular, path: '/survey-status' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { text: 'Admin Management', icon: Shield24Regular, path: '/admin-management' },
      { text: 'Settings', icon: Settings24Regular, path: '/settings' },
    ],
  },
];

const useStyles = makeStyles({
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: 'var(--color-background)',
  },
  sidebar: {
    width: 'var(--sidebar-width)',
    backgroundColor: 'var(--color-surface)',
    borderRight: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column',
    transitionProperty: 'width',
    transitionDuration: '200ms',
    transitionTimingFunction: 'ease',
    position: 'relative',
    flexShrink: 0,
    // On mobile the sidebar becomes an off-canvas drawer.
    [MOBILE]: {
      position: 'fixed',
      top: 0,
      left: 0,
      bottom: 0,
      height: '100vh',
      width: 'var(--sidebar-width)',
      zIndex: 1000,
      transform: 'translateX(-100%)',
      transitionProperty: 'transform',
      boxShadow: 'var(--shadow-lg)',
    },
  },
  sidebarCollapsed: {
    width: 'var(--sidebar-collapsed-width)',
    // Ignore desktop collapse when the drawer is shown on mobile.
    [MOBILE]: {
      width: 'var(--sidebar-width)',
    },
  },
  sidebarMobileOpen: {
    [MOBILE]: {
      transform: 'translateX(0)',
    },
  },
  overlay: {
    display: 'none',
    [MOBILE]: {
      display: 'block',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      zIndex: 999,
    },
  },
  topbar: {
    display: 'none',
    [MOBILE]: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-sm)',
      padding: 'var(--space-sm) var(--space-md)',
      borderBottom: '1px solid var(--color-border)',
      backgroundColor: 'var(--color-surface)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: '56px',
      boxSizing: 'border-box',
      zIndex: 100,
    },
  },
  brandArea: {
    padding: 'var(--space-lg) var(--space-md)',
    borderBottom: '1px solid var(--color-border-light)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
    minHeight: '64px',
  },
  logoImg: {
    height: '44px',
    width: 'auto',
    objectFit: 'contain',
    flexShrink: 0,
    display: 'block',
  },
  brandText: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  nav: {
    flex: 1,
    paddingTop: 'var(--space-md)',
    paddingBottom: 'var(--space-md)',
    overflowY: 'auto',
  },
  sectionLabel: {
    padding: '0 var(--space-md)',
    marginTop: 'var(--space-md)',
    marginBottom: 'var(--space-xs)',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--color-text-tertiary)',
  },
  navItem: {
    width: 'calc(100% - 16px)',
    margin: '1px 8px',
    justifyContent: 'flex-start',
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    minHeight: '40px',
    color: 'var(--color-text-secondary)',
    fontWeight: '400',
    ':hover': {
      backgroundColor: 'var(--color-background-hover)',
      color: 'var(--color-text)',
    },
  },
  navItemActive: {
    width: 'calc(100% - 16px)',
    margin: '1px 8px',
    justifyContent: 'flex-start',
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    minHeight: '40px',
    backgroundColor: 'var(--undp-primary-light)',
    color: 'var(--undp-primary)',
    fontWeight: '500',
    ':hover': {
      backgroundColor: 'var(--undp-primary-light)',
    },
  },
  navItemCollapsed: {
    justifyContent: 'center',
    padding: '10px',
  },
  navItemLabel: {
    marginLeft: 'var(--space-sm)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  userArea: {
    padding: 'var(--space-md)',
    borderTop: '1px solid var(--color-border-light)',
  },
  userButton: {
    width: '100%',
    justifyContent: 'flex-start',
    padding: 'var(--space-sm) var(--space-sm)',
    borderRadius: 'var(--radius-sm)',
  },
  userMenuAction: {
    width: '100%',
    justifyContent: 'flex-start',
    padding: '10px var(--space-md)',
    borderRadius: 'var(--radius-sm)',
  },
  collapseBtn: {
    position: 'absolute',
    right: '-12px',
    top: '72px',
    width: '24px',
    height: '24px',
    minWidth: '24px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    boxShadow: 'var(--shadow-sm)',
    padding: '0',
    zIndex: 10,
    ':hover': {
      backgroundColor: 'var(--color-background-hover)',
    },
    [MOBILE]: {
      display: 'none',
    },
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  pageContent: {
    flex: 1,
    maxWidth: 'var(--content-max-width)',
    width: '100%',
    margin: '0 auto',
    padding: 'var(--space-lg)',
    [MOBILE]: {
      // Fixed top bar is 56px tall — push content below it.
      paddingTop: 'calc(56px + var(--space-md))',
      paddingRight: 'var(--space-md)',
      paddingBottom: 'var(--space-md)',
      paddingLeft: 'var(--space-md)',
    },
  },
});

export default function Layout({ children }: LayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const styles = useStyles();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={styles.root}>
      {mobileNavOpen && (
        <div className={styles.overlay} onClick={() => setMobileNavOpen(false)} />
      )}
      <div className={`${styles.sidebar} ${sidebarCollapsed ? styles.sidebarCollapsed : ''} ${mobileNavOpen ? styles.sidebarMobileOpen : ''}`}>
        {/* Brand */}
        <div className={styles.brandArea}>
          <img
            className={styles.logoImg}
            src={`${process.env.PUBLIC_URL}/UNDP.png`}
            alt="UNDP"
          />
          {!sidebarCollapsed && (
            <div className={styles.brandText}>
              <Text size={400} weight="semibold" style={{ color: 'var(--color-text)', lineHeight: 1.2 }}>
                UNDP PIA
              </Text>
              <Text size={200} style={{ color: 'var(--color-text-tertiary)', lineHeight: 1.2 }}>
                Platform
              </Text>
            </div>
          )}
        </div>

        {/* Collapse toggle */}
        <Button
          className={styles.collapseBtn}
          appearance="subtle"
          size="small"
          icon={sidebarCollapsed ? <ChevronRight20Regular /> : <ChevronLeft20Regular />}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        {/* Navigation */}
        <nav className={styles.nav}>
          {navSections.map((section, sIdx) => (
            <div key={section.label}>
              {sIdx > 0 && <Divider style={{ margin: 'var(--space-sm) var(--space-md)' }} />}
              {!sidebarCollapsed && (
                <div className={styles.sectionLabel}>{section.label}</div>
              )}
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                const IconComponent = item.icon;
                return (
                  <Button
                    key={item.text}
                    appearance="subtle"
                    icon={<IconComponent />}
                    onClick={() => { navigate(item.path); setMobileNavOpen(false); }}
                    className={`${isActive ? styles.navItemActive : styles.navItem} ${sidebarCollapsed ? styles.navItemCollapsed : ''}`}
                  >
                    {!sidebarCollapsed && (
                      <span className={styles.navItemLabel}>{item.text}</span>
                    )}
                  </Button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User area */}
        <div className={styles.userArea}>
          <Popover
            open={userMenuOpen}
            onOpenChange={(_, data) => setUserMenuOpen(data.open)}
            positioning="above-start"
          >
            <PopoverTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                className={styles.userButton}
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                {sidebarCollapsed ? (
                  <Persona size="small" name={user?.full_name || user?.email || 'Admin'} avatar={{ color: 'brand' }} />
                ) : (
                  <Persona
                    name={user?.full_name || user?.email || 'Admin'}
                    secondaryText={user?.email}
                    size="medium"
                    avatar={{ color: 'brand' }}
                  />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverSurface style={{ padding: 'var(--space-xs)', minWidth: '200px' }}>
              <Button
                appearance="subtle"
                icon={<Settings24Regular />}
                className={styles.userMenuAction}
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/settings');
                }}
              >
                Account Settings
              </Button>
              <Button
                appearance="subtle"
                icon={<SignOut24Regular />}
                className={styles.userMenuAction}
                onClick={() => {
                  setUserMenuOpen(false);
                  handleLogout();
                }}
                style={{ color: 'var(--color-error)' }}
              >
                Sign Out
              </Button>
            </PopoverSurface>
          </Popover>
        </div>
      </div>

      {/* Main content */}
      <div className={styles.main}>
        {/* Mobile top bar with hamburger */}
        <div className={styles.topbar}>
          <Button
            appearance="subtle"
            icon={<Navigation24Regular />}
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation"
          />
          <img
            src={`${process.env.PUBLIC_URL}/UNDP.png`}
            alt="UNDP"
            style={{ height: '32px', width: 'auto', display: 'block' }}
          />
          <Text size={400} weight="semibold" style={{ color: 'var(--color-text)' }}>
            UNDP PIA
          </Text>
        </div>
        <main className={styles.pageContent}>
          {children}
        </main>
      </div>
    </div>
  );
}
